import { useState, useEffect } from 'react';

const DEFAULT_DOCUMENTS = [
  {
    id: 'lfsaa-aml-policy',
    name: 'AML Policy',
    icon: 'fas fa-shield-alt',
    filename: 'LFSA - AML Policy.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-client-agreement',
    name: 'Client Agreement',
    icon: 'fas fa-file-contract',
    filename: 'LFSA - Client Agreement.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-complaints-handling-procedure',
    name: 'Complaints Handling Procedure',
    icon: 'fas fa-comment-dots',
    filename: 'LFSA - Complaints Handling Procedure.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-conflict-of-interest-policy',
    name: 'Conflict of Interest Policy',
    icon: 'fas fa-balance-scale',
    filename: 'LFSA - CONFLICT OF INTEREST POLICY.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-deposit-refund-policy',
    name: 'Deposit & Refund Policy',
    icon: 'fas fa-money-bill-wave',
    filename: 'LFSA - Deposit & Refund policy.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-order-execution-policy',
    name: 'Order Execution Policy',
    icon: 'fas fa-chart-bar',
    filename: 'LFSA - Order Execution Policy.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-privacy-policy',
    name: 'Privacy Policy',
    icon: 'fas fa-lock',
    filename: 'LFSA - Privacy Policy.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-regulations',
    name: 'Regulations',
    icon: 'fas fa-gavel',
    filename: 'LFSA - Regulations.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-swaps-policy',
    name: 'Swaps Policy',
    icon: 'fas fa-exchange-alt',
    filename: 'LFSA - Swaps Policy.pdf',
    category: 'other'
  },
  {
    id: 'lfsaa-terms-and-conditions',
    name: 'Terms and Conditions',
    icon: 'fas fa-file-alt',
    filename: 'LFSA - Terms and Conditions.pdf',
    category: 'other'
  }
]

export const usePDFDocuments = () => {
  const normalizeDocumentFilename = (filename = '') => {
    // Migrate legacy .doc/.docx names to deployed PDF documents.
    return filename.replace(/\.(docx?|DOCX?)$/, '.pdf');
  };

  const normalizeDocuments = (docs = []) => docs.map((doc) => ({
    ...doc,
    filename: normalizeDocumentFilename(doc.filename || '')
  }));

  const [documents, setDocuments] = useState(() => normalizeDocuments(DEFAULT_DOCUMENTS));
  const [isLoading, setIsLoading] = useState(false);

  const getDocumentIcon = (filename = '') => {
    const lower = filename.toLowerCase();
    if (lower.includes('privacy')) return 'fas fa-lock';
    if (lower.includes('aml')) return 'fas fa-shield-alt';
    if (lower.includes('risk')) return 'fas fa-exclamation-triangle';
    if (lower.includes('agreement') || lower.includes('terms')) return 'fas fa-file-contract';
    if (lower.includes('compensation') || lower.includes('fund')) return 'fas fa-university';
    if (lower.includes('kid')) return 'fas fa-file-alt';
    return 'fas fa-file-pdf';
  };

  const filenameToId = (filename = '') =>
    filename
      .toLowerCase()
      .replace(/\.pdf$/i, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

  const buildDocumentsFromFilenames = (filenames = []) => {
    return filenames
      .filter((name) => name.toLowerCase().endsWith('.pdf'))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .map((filename) => ({
        id: filenameToId(filename),
        name: filename.replace(/\.pdf$/i, ''),
        icon: getDocumentIcon(filename),
        filename,
        category: filename.toUpperCase().startsWith('SVG') ? 'SVG' : 'other'
      }));
  };

  const parseDirectoryListingFilenames = (html = '') => {
    const hrefPattern = /href="([^"]+)"/g;
    const files = [];
    let match;

    while ((match = hrefPattern.exec(html)) !== null) {
      const href = match[1];
      if (!href || href === '../' || href.endsWith('/')) {
        continue;
      }

      try {
        const lastPathSegment = href.split('/').pop() || '';
        const decoded = decodeURIComponent(lastPathSegment);
        if (decoded.toLowerCase().endsWith('.pdf')) {
          files.push(decoded);
        }
      } catch (error) {
        // Ignore malformed href entries from directory listing.
      }
    }

    return Array.from(new Set(files));
  };

  // Load documents from localStorage on component mount
  useEffect(() => {
    const loadDocuments = async () => {
      // Priority 1: read the real document files exposed by nginx.
      try {
        const response = await fetch('/documents/', { cache: 'no-store' });
        if (response.ok) {
          const html = await response.text();
          const folderFilenames = parseDirectoryListingFilenames(html);
          if (folderFilenames.length > 0) {
            setDocuments(buildDocumentsFromFilenames(folderFilenames));
            return;
          }
        }
      } catch (error) {
        // Fall back to existing behavior when directory listing is unavailable.
      }

      // Priority 2: admin-managed localStorage documents.
      const savedDocuments = localStorage.getItem('adminPDFDocuments');
      if (savedDocuments) {
        try {
          const parsed = JSON.parse(savedDocuments);
          setDocuments(normalizeDocuments(parsed));
          return;
        } catch (error) {
          console.error('Error loading saved PDF documents:', error);
        }
      }

      // Priority 3: static fallback list.
      setDocuments(normalizeDocuments(DEFAULT_DOCUMENTS));
    };

    loadDocuments();
  }, []);

  // Save documents to localStorage whenever documents change
  useEffect(() => {
    localStorage.setItem('adminPDFDocuments', JSON.stringify(documents));
    
    // Dispatch custom event to notify other components of document changes
    window.dispatchEvent(new CustomEvent('pdfDocumentsUpdated', {
      detail: { documents }
    }));
  }, [documents]);

  // Add or update a document
  const updateDocument = (documentData) => {
    setIsLoading(true);
    
    setDocuments(prevDocs => {
      const existingIndex = prevDocs.findIndex(doc => doc.id === documentData.id);
      
      if (existingIndex >= 0) {
        // Update existing document
        const updatedDocs = [...prevDocs];
        updatedDocs[existingIndex] = {
          ...updatedDocs[existingIndex],
          ...documentData,
          filename: normalizeDocumentFilename(documentData.filename || updatedDocs[existingIndex].filename)
        };
        return updatedDocs;
      } else {
        // Add new document
        return [
          ...prevDocs,
          {
            ...documentData,
            filename: normalizeDocumentFilename(documentData.filename || '')
          }
        ];
      }
    });
    
    setIsLoading(false);
  };

  // Delete a document
  const deleteDocument = (documentId) => {
    setIsLoading(true);
    
    setDocuments(prevDocs => 
      prevDocs.filter(doc => doc.id !== documentId)
    );
    
    setIsLoading(false);
  };

  // Add a new custom document
  const addDocument = (documentData) => {
    const newId = documentData.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
    const newDocument = {
      id: newId,
      name: documentData.name.replace(/\.pdf$/i, ''),
      icon: documentData.icon ,
      filename: normalizeDocumentFilename(documentData.filename || ''),
      category: documentData.category 
    };
    
    updateDocument(newDocument);
  };

  // Get document by ID
  const getDocument = (documentId) => {
    return documents.find(doc => doc.id === documentId);
  };

  // Get documents by category
  const getDocumentsByCategory = (category) => {
    return documents.filter(doc => doc.category === category);
  };

  // Get document URL
  const getDocumentUrl = (document) => {
    const safeFilename = encodeURIComponent(normalizeDocumentFilename(document.filename || ''));
    return `/documents/${safeFilename}`;
  };

  // Reset to defaults
  const resetToDefaults = () => {
    setDocuments(normalizeDocuments(DEFAULT_DOCUMENTS));
  };

  return {
    documents,
    isLoading,
    updateDocument,
    deleteDocument,
    addDocument,
    getDocument,
    getDocumentsByCategory,
    getDocumentUrl,
    resetToDefaults
  };
};